// Hand a file or folder over to someone else of the install: its owner may,
// and so may whoever may hand over anyone's (SCOPE.HAND_OVER_ANY) — how the
// organisation takes back what someone who left made (the HR space, say). The new owner can do everything with it; the
// previous one keeps only what is shared with them. A folder can take along
// what the previous owner owns inside, folders that don't inherit access
// included; what others own there stays theirs.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { BaseModal } from '@/shared/ui/BaseModal.jsx';
import { usePostRequest } from '@/shared/hooks/requests.js';
import { NODE_OWNER } from '@/shared/utils/urls.js';
import { nameOf } from '../people.js';
import { PeoplePicker } from './SharingModal.jsx';

export const TransferOwnerModal = ({ open, setOpen, node, onDone }) => {
  const { t } = useTranslation();
  const [person, setPerson] = useState(null);
  const [withContents, setWithContents] = useState(true);
  const transfer = usePostRequest();
  const isFolder = node?.type === 'folder' || node?.nodeType === 'folder' || node?.node_type === 'folder';
  const ownerUuid = node?.ownerUuid ?? node?.owner_uuid;

  useEffect(() => {
    if (open) {
      setPerson(null);
      setWithContents(true);
    }
  }, [open, node?.uuid]);

  const submit = async () => {
    const { success } = await transfer.request({
      url: NODE_OWNER.replace('{node_uuid}', node.uuid),
      data: { userUuid: person.uuid, includeContents: isFolder && withContents },
    });
    if (success) {
      toast.success(t('my-docs:owner.done', { name: nameOf(person) }));
      setOpen(false);
      onDone?.();
    }
  };

  return (
    <BaseModal
      open={open}
      onOpenChange={setOpen}
      title={t('my-docs:owner.title', { name: node?.name || '' })}
      description={t('my-docs:owner.text')}
      footer={(
        <>
          <Button variant="outline" onClick={() => setOpen(false)}>{t('my-docs:owner.cancel')}</Button>
          <Button disabled={!person || transfer.loading} onClick={submit} data-testid="owner-submit">
            {t('my-docs:owner.submit')}
          </Button>
        </>
      )}
    >
      <div className="space-y-3">
        {person ? (
          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid="owner-chosen">
            <span>
              <span className="text-muted-foreground">{t('my-docs:owner.chosen')}: </span>
              <span className="font-medium">{nameOf(person)}</span>
            </span>
            <button type="button" aria-label={t('my-docs:owner.change')} onClick={() => setPerson(null)}>
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <PeoplePicker t={t} exclude={ownerUuid != null ? [String(ownerUuid)] : []} selected={[]} onSelect={setPerson} />
        )}
        {isFolder && (
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={withContents}
              onCheckedChange={(value) => setWithContents(value === true)}
              data-testid="owner-with-contents"
            />
            <span>
              {t('my-docs:owner.withContents')}
              <span className="block text-xs text-muted-foreground">{t('my-docs:owner.withContentsHint')}</span>
            </span>
          </label>
        )}
      </div>
    </BaseModal>
  );
};
